import React, { useCallback, useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scissors, Sparkles, Brush, MapPin, Phone, Instagram, Menu, X, MessageCircle, Star, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
const galleryImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/55Vun4o1i4GZBZEdBjncLt/1.png',
  alt: 'Balayage cobrizo vibrante',
  label: 'Balayage Cobrizo'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/x6f1uKeiLyXaF65aCRa8Sy/2.png',
  alt: 'Ondas castaño con highlights',
  label: 'Highlights Naturales'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/aY4wr1zFUuPUmm5dbkHjBk/3.png',
  alt: 'Maquillaje profesional',
  label: 'Maquillaje Profesional'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/2kuoMZcYYyqSB9fLo4kpZs/4.png',
  alt: 'Tratamiento facial spa',
  label: 'Facial Premium'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/mFppqTTzgzm7R3PCfqmkQs/5.png',
  alt: 'Tratamiento capilar',
  label: 'Tratamiento Capilar'
}];
function GalleryCarousel() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const next = useCallback(() => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % galleryImages.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, []);
  useEffect(() => {
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next]);
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.9,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    })
  };
  const prevIdx = (current - 1 + galleryImages.length) % galleryImages.length;
  const nextIdx = (current + 1) % galleryImages.length;
  return <div className="relative" data-id="element-0">
      <div className="flex items-center justify-center gap-4 md:gap-6" data-id="element-1">
        {/* Left preview — desktop only */}
        <div className="hidden md:block w-48 lg:w-56 aspect-[3/4] rounded-sm overflow-hidden opacity-30 cursor-pointer shrink-0 transition-opacity hover:opacity-50" onClick={prev} data-id="element-2">
          <img src={galleryImages[prevIdx].src} alt={galleryImages[prevIdx].alt} className="w-full h-full object-cover" data-id="element-3" />
        </div>

        {/* Center — main image */}
        <div className="relative w-full max-w-md md:max-w-lg aspect-[3/4] overflow-hidden rounded-sm border border-montalvo-gold/20" data-id="element-4">
          <AnimatePresence mode="wait" custom={direction} data-id="element-5">
            <motion.div key={current} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="absolute inset-0" data-id="element-6">
              <img src={galleryImages[current].src} alt={galleryImages[current].alt} className="w-full h-full object-cover" data-id="element-7" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 pt-16" data-id="element-8">
                <p className="text-white font-serif text-xl" data-id="element-9">
                  {galleryImages[current].label}
                </p>
                <p className="text-montalvo-gold text-sm font-light mt-1" data-id="element-10">
                  @montalvo_salonspa
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="absolute inset-0 border border-montalvo-gold/10 rounded-sm pointer-events-none" data-id="element-11"></div>
        </div>

        {/* Right preview — desktop only */}
        <div className="hidden md:block w-48 lg:w-56 aspect-[3/4] rounded-sm overflow-hidden opacity-30 cursor-pointer shrink-0 transition-opacity hover:opacity-50" onClick={next} data-id="element-12">
          <img src={galleryImages[nextIdx].src} alt={galleryImages[nextIdx].alt} className="w-full h-full object-cover" data-id="element-13" />
        </div>
      </div>

      {/* Navigation arrows */}
      <button onClick={prev} className="absolute left-2 md:left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/60 backdrop-blur-sm border border-montalvo-gold/30 rounded-full text-white hover:border-montalvo-gold hover:text-montalvo-gold transition-all" aria-label="Imagen anterior" data-id="element-14">
        <ChevronLeftIcon className="w-5 h-5" data-id="element-15" />
      </button>
      <button onClick={next} className="absolute right-2 md:right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/60 backdrop-blur-sm border border-montalvo-gold/30 rounded-full text-white hover:border-montalvo-gold hover:text-montalvo-gold transition-all" aria-label="Siguiente imagen" data-id="element-16">
        <ChevronRightIcon className="w-5 h-5" data-id="element-17" />
      </button>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2.5 mt-8" data-id="element-18">
        {galleryImages.map((_, idx) => <button key={idx} onClick={() => {
        setDirection(idx > current ? 1 : -1);
        setCurrent(idx);
      }} className={`transition-all duration-300 rounded-full ${idx === current ? 'w-8 h-2 bg-montalvo-gold' : 'w-2 h-2 bg-white/20 hover:bg-white/40'}`} aria-label={`Ver imagen ${idx + 1}`} data-id="element-19" />)}
      </div>
    </div>;
}
export function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [notifIndex, setNotifIndex] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const [notifDismissed, setNotifDismissed] = useState(false);
  const socialProofClients = [{
    name: 'Carolina Mendoza',
    service: 'reservó Balayage Premium',
    time: 'hace 5 minutos ⭐'
  }, {
    name: 'Valeria Torres',
    service: 'agendó Spa Facial',
    time: 'hace 12 minutos ⭐'
  }, {
    name: 'Gabriela Ríos',
    service: 'reservó Maquillaje Pro',
    time: 'hace 18 minutos ⭐'
  }];
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (notifDismissed) return;
    // Show first notification after 4 seconds
    const initialDelay = setTimeout(() => {
      setShowNotif(true);
    }, 4000);
    // Hide after 6s, then cycle: hide → wait 2s → show next → wait 6s → hide → repeat
    // Total cycle = 6s visible + 2s gap + ... repeats every 15s
    const cycle = setInterval(() => {
      setShowNotif(false);
      setTimeout(() => {
        setNotifIndex(prev => (prev + 1) % 3);
        setShowNotif(true);
        // Auto-hide this one after 6 seconds
        setTimeout(() => setShowNotif(false), 6000);
      }, 2000);
    }, 15000);
    // Auto-hide the first one after 6 seconds
    const firstHide = setTimeout(() => {
      setShowNotif(false);
    }, 10000); // 4s delay + 6s visible
    return () => {
      clearTimeout(initialDelay);
      clearTimeout(firstHide);
      clearInterval(cycle);
    };
  }, [notifDismissed]);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);
  return <div className="min-h-screen bg-montalvo-black text-white selection:bg-montalvo-gold selection:text-black" data-id="element-20">
      {/* HEADER */}
      <header className="fixed top-0 w-full z-50 bg-montalvo-black/80 backdrop-blur-md border-b border-montalvo-gold/20" data-id="element-21">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-24 flex items-center justify-between" data-id="element-22">
          <div className="flex-shrink-0 z-50" data-id="element-23">
            <img src="https://cdn.magicpatterns.com/uploads/m853VAxGa18jUZTjFLvchJ/logomontalvo.png" alt="Montalvo Salón & Spa Logo" className="h-16 w-auto object-contain" data-id="element-24" />
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8" data-id="element-25">
            <nav className="flex items-center gap-8 text-sm font-medium tracking-widest uppercase" data-id="element-26">
              <a href="#servicios" className="hover:text-montalvo-gold transition-colors" data-id="element-27">
                Servicios
              </a>
              <a href="#galeria" className="hover:text-montalvo-gold transition-colors" data-id="element-28">
                Galería
              </a>
              <a href="#sedes" className="hover:text-montalvo-gold transition-colors" data-id="element-29">
                Sedes
              </a>
              <a href="#contacto" className="hover:text-montalvo-gold transition-colors" data-id="element-30">
                Contacto
              </a>
            </nav>
            <a href="#reserva" className="bg-montalvo-gold hover:bg-montalvo-goldBright text-black px-6 py-3 rounded-sm text-sm font-bold uppercase tracking-wider transition-all duration-300" data-id="element-31">
              Reserva Tu Cita
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden flex items-center gap-4 z-50" data-id="element-32">
            <button onClick={toggleMenu} className="text-montalvo-gold p-2" data-id="element-33">
              {isMenuOpen ? <X size={28} data-id="element-34" /> : <Menu size={28} data-id="element-35" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence data-id="element-36">
          {isMenuOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: '100vh',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="md:hidden fixed inset-0 top-24 bg-montalvo-black border-t border-montalvo-gold/20 overflow-y-auto" data-id="element-37">
              <nav className="flex flex-col px-6 py-12 gap-8 text-xl font-serif text-center" data-id="element-38">
                <a href="#servicios" onClick={closeMenu} className="hover:text-montalvo-gold" data-id="element-39">
                  Servicios
                </a>
                <a href="#galeria" onClick={closeMenu} className="hover:text-montalvo-gold" data-id="element-40">
                  Galería
                </a>
                <a href="#sedes" onClick={closeMenu} className="hover:text-montalvo-gold" data-id="element-41">
                  Sedes
                </a>
                <a href="#contacto" onClick={closeMenu} className="hover:text-montalvo-gold" data-id="element-42">
                  Contacto
                </a>
                <a href="#reserva" onClick={closeMenu} className="mt-8 bg-montalvo-gold text-black px-6 py-4 rounded-sm text-lg font-bold uppercase tracking-wider font-sans" data-id="element-43">
                  Reserva Tu Cita
                </a>
              </nav>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* SECTION 1 — HERO */}
      <section className="relative h-screen min-h-[600px] flex items-center pt-24 overflow-hidden" data-id="element-44">
        <div className="absolute inset-0 z-0" data-id="element-45">
          <img src="https://cdn.magicpatterns.com/uploads/kZwrs7J9JUgwEX2zzubWzm/bannerMontalvo.png" alt="Montalvo Salón & Spa" className="w-full h-full object-cover object-center" data-id="element-46" />
          <div className="absolute inset-0 bg-gradient-to-r from-montalvo-black via-montalvo-black/80 to-transparent" data-id="element-47"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-48">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl" data-id="element-49">
            <motion.h1 variants={fadeIn} className="text-5xl md:text-7xl lg:text-8xl font-serif leading-tight mb-6" data-id="element-50">
              Donde la Belleza <br data-id="element-51" />
              se convierte en <span className="text-montalvo-gold" data-id="element-52">Arte</span>
            </motion.h1>

            <motion.p variants={fadeIn} className="text-2xl md:text-3xl text-montalvo-gold font-serif italic mb-10" data-id="element-53">
              "El salón premium que Lima eligió"
            </motion.p>

            <motion.div variants={fadeIn} className="flex flex-col sm:flex-row gap-4 mb-16" data-id="element-54">
              <a href="#reserva" className="inline-flex items-center justify-center bg-montalvo-gold hover:bg-montalvo-goldBright text-black px-8 py-4 rounded-sm text-sm font-bold uppercase tracking-widest transition-all duration-300" data-id="element-55">
                ✦ Reserva Tu Cita
              </a>
              <a href="#servicios" className="inline-flex items-center justify-center border border-white hover:border-montalvo-gold hover:text-montalvo-gold bg-transparent px-8 py-4 rounded-sm text-sm font-bold uppercase tracking-widest transition-all duration-300" data-id="element-56">
                Conoce Nuestros Servicios
              </a>
            </motion.div>

            <motion.div variants={fadeIn} className="inline-flex items-center gap-3 text-montalvo-gold font-medium tracking-wider text-sm uppercase" data-id="element-57">
              <Star className="w-5 h-5 fill-montalvo-gold" data-id="element-58" />
              <span data-id="element-59">+50 Sedes • 25 Años • Lima, Perú</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 — SERVICES */}
      <section id="servicios" className="py-24 bg-montalvo-dark1 relative" data-id="element-60">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-montalvo-gold/50 to-transparent" data-id="element-61"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-62">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-63">
            <h2 className="text-4xl md:text-5xl font-serif text-montalvo-gold mb-6" data-id="element-64">
              Nuestros Servicios Premium
            </h2>
            <div className="w-24 h-px bg-montalvo-gold mx-auto" data-id="element-65"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-66">
            {[{
            icon: <Scissors className="w-8 h-8 text-montalvo-gold" data-id="element-67" />,
            title: 'Hair & Styling',
            desc: 'Cortes, Balayage y tratamientos de lujo'
          }, {
            icon: <Sparkles className="w-8 h-8 text-montalvo-gold" data-id="element-68" />,
            title: 'Spa & Facial',
            desc: 'Tratamientos faciales y relajación total'
          }, {
            icon: <Sparkles className="w-8 h-8 text-montalvo-gold" data-id="element-69" />,
            title: 'Nail Art Premium',
            desc: 'Manicure y pedicure de nivel internacional'
          }, {
            icon: <Brush className="w-8 h-8 text-montalvo-gold" data-id="element-70" />,
            title: 'Maquillaje Profesional',
            desc: 'Looks para cada ocasión especial'
          }].map((service, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-montalvo-black p-8 rounded-sm border border-transparent hover:border-montalvo-gold transition-all duration-300 group text-center" data-id="element-71">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-montalvo-dark1 border border-montalvo-gold/20 mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-72">
                  {service.icon}
                </div>
                <h3 className="text-xl font-serif text-white mb-3" data-id="element-73">
                  {service.title}
                </h3>
                <p className="text-gray-400 font-light text-sm leading-relaxed" data-id="element-74">
                  {service.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SECTION 3 — SOCIAL PROOF BANNER */}
      <section className="py-16 bg-montalvo-strip border-y border-montalvo-gold/30" data-id="element-75">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-76">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 divide-y md:divide-y-0 md:divide-x divide-montalvo-gold/30 text-center" data-id="element-77">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="py-4 md:py-0" data-id="element-78">
              <div className="text-5xl md:text-6xl font-serif text-montalvo-gold mb-2" data-id="element-79">
                50+
              </div>
              <div className="text-sm font-medium tracking-widest uppercase text-white" data-id="element-80">
                Sedes en Perú
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="py-4 md:py-0" data-id="element-81">
              <div className="text-5xl md:text-6xl font-serif text-montalvo-gold mb-2" data-id="element-82">
                25
              </div>
              <div className="text-sm font-medium tracking-widest uppercase text-white" data-id="element-83">
                Años de Experiencia
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="py-4 md:py-0" data-id="element-84">
              <div className="text-5xl md:text-6xl font-serif text-montalvo-gold mb-2" data-id="element-85">
                111K
              </div>
              <div className="text-sm font-medium tracking-widest uppercase text-white" data-id="element-86">
                Seguidores en Instagram
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — GALLERY */}
      <section id="galeria" className="py-24 bg-montalvo-black relative" data-id="element-87">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-montalvo-gold/50 to-transparent" data-id="element-88"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-89">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-90">
            <h2 className="text-4xl md:text-5xl font-serif text-montalvo-gold mb-4" data-id="element-91">
              Transformaciones Reales
            </h2>
            <p className="text-sm font-medium tracking-widest uppercase text-white" data-id="element-92">
              El arte habla por sí solo
            </p>
          </motion.div>

          <GalleryCarousel data-id="element-93" />

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-center mt-12" data-id="element-94">
            <a href="https://instagram.com/montalvo_salonspa" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-montalvo-gold hover:text-montalvo-goldBright transition-colors font-medium" data-id="element-95">
              <Instagram className="w-5 h-5" data-id="element-96" />
              Ver más en @montalvo_salonspa
            </a>
          </motion.div>
        </div>
      </section>

      {/* SECTION 5 — FIND YOUR SEDE */}
      <section id="sedes" className="py-24 bg-montalvo-dark2 relative border-y border-montalvo-gold/20" data-id="element-97">
        <div className="absolute inset-4 border border-montalvo-gold/10 pointer-events-none" data-id="element-98"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-99">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-100">
            <motion.h2 variants={fadeIn} className="text-4xl md:text-5xl font-serif text-white mb-4" data-id="element-101">
              Encuentra Tu Sede Más Cercana
            </motion.h2>
            <motion.p variants={fadeIn} className="text-xl font-serif italic text-montalvo-gold mb-12" data-id="element-102">
              Más de 50 centros en todo el Perú
            </motion.p>
            <motion.div variants={fadeIn} data-id="element-103">
              <a href="#" className="inline-flex items-center justify-center gap-3 bg-montalvo-gold hover:bg-montalvo-goldBright text-black px-10 py-5 rounded-sm text-lg font-bold uppercase tracking-widest transition-all duration-300" data-id="element-104">
                <MapPin className="w-6 h-6" data-id="element-105" />
                Ver Todas las Sedes
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 6 — CONTACT & BOOKING */}
      <section id="contacto" className="py-24 bg-montalvo-black relative" data-id="element-106">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-107">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-108">
            <motion.h2 variants={fadeIn} className="text-4xl md:text-6xl font-serif text-montalvo-gold mb-4" data-id="element-109">
              Reserva Tu Experiencia
            </motion.h2>
            <motion.p variants={fadeIn} className="text-sm font-medium tracking-widest uppercase text-white mb-16" data-id="element-110">
              San Miguel, Lima, Perú
            </motion.p>

            <motion.div variants={fadeIn} className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-16 mb-16" data-id="element-111">
              <div className="flex flex-col items-center gap-3" data-id="element-112">
                <Phone className="w-8 h-8 text-montalvo-gold" data-id="element-113" />
                <span className="text-xl font-serif text-white" data-id="element-114">
                  932 824 708
                </span>
              </div>
              <div className="flex flex-col items-center gap-3" data-id="element-115">
                <MapPin className="w-8 h-8 text-montalvo-gold" data-id="element-116" />
                <span className="text-xl font-serif text-white" data-id="element-117">
                  San Miguel, Lima
                </span>
              </div>
              <div className="flex flex-col items-center gap-3" data-id="element-118">
                <Instagram className="w-8 h-8 text-montalvo-gold" data-id="element-119" />
                <span className="text-xl font-serif text-white" data-id="element-120">
                  @montalvo_salonspa
                </span>
              </div>
            </motion.div>

            <motion.div variants={fadeIn} data-id="element-121">
              <a href="https://wa.me/51932824708" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-montalvo-gold hover:bg-montalvo-goldBright text-black px-10 py-5 rounded-sm text-lg font-bold uppercase tracking-widest transition-all duration-300 w-full md:w-auto" data-id="element-122">
                <MessageCircle className="w-6 h-6" data-id="element-123" />
                Contáctanos por WhatsApp
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-montalvo-black border-t border-montalvo-gold/20 py-12" data-id="element-124">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-125">
          <img src="https://cdn.magicpatterns.com/uploads/m853VAxGa18jUZTjFLvchJ/logomontalvo.png" alt="Montalvo Logo" className="h-12 w-auto opacity-80" data-id="element-126" />
          <div className="text-sm text-gray-500 font-light text-center md:text-left" data-id="element-127">
            &copy; {new Date().getFullYear()} Montalvo Salón & Spa. Todos los
            derechos reservados.
          </div>
          <div className="flex gap-4" data-id="element-128">
            <a href="#" className="text-montalvo-gold hover:text-montalvo-goldBright transition-colors" data-id="element-129">
              <Instagram className="w-6 h-6" data-id="element-130" />
            </a>
          </div>
        </div>
      </footer>

      {/* FLOATING WHATSAPP BUTTON */}
      <a href="https://wa.me/51932824708" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 bg-montalvo-gold text-black pl-5 pr-6 py-3 rounded-full shadow-[0_0_20px_rgba(201,169,110,0.3)] hover:scale-105 transition-transform duration-300 flex items-center gap-2" data-id="element-131">
        <MessageCircle size={24} data-id="element-132" />
        <span className="text-sm font-bold uppercase tracking-wider" data-id="element-133">
          Reserva Aquí ✦
        </span>
      </a>

      {/* SOCIAL PROOF NOTIFICATION */}
      <AnimatePresence data-id="element-134">
        {showNotif && !notifDismissed && <motion.div initial={{
        opacity: 0,
        y: 80,
        x: -20
      }} animate={{
        opacity: 1,
        y: 0,
        x: 0
      }} exit={{
        opacity: 0,
        y: 80,
        x: -20
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="fixed bottom-5 left-5 z-[9999] w-[280px] rounded-xl p-3 px-4" style={{
        background: '#1A1A1A',
        border: '1px solid #C9A96E',
        boxShadow: '0 4px 20px rgba(201, 169, 110, 0.15)'
      }} data-id="element-135">
            {/* Green pulse dot */}
            <span className="absolute top-2 left-2 flex h-2 w-2" data-id="element-136">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-137"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" data-id="element-138"></span>
            </span>

            {/* Close button */}
            <button onClick={() => {
          setShowNotif(false);
          setNotifDismissed(true);
          setTimeout(() => setNotifDismissed(false), 30000);
        }} className="absolute top-2 right-3 text-[#888] hover:text-montalvo-gold transition-colors text-base leading-none" aria-label="Cerrar" data-id="element-139">
              ×
            </button>

            <div className="flex items-center gap-3" data-id="element-140">
              {/* Logo circle */}
              <div className="w-12 h-12 rounded-full shrink-0 flex items-center justify-center overflow-hidden" style={{
            border: '2px solid #C9A96E',
            background: '#0A0A0A'
          }} data-id="element-141">
                <img src="https://cdn.magicpatterns.com/uploads/m853VAxGa18jUZTjFLvchJ/logomontalvo.png" alt="Montalvo" className="w-8 h-8 object-contain" data-id="element-142" />
              </div>

              {/* Text content */}
              <div className="min-w-0" data-id="element-143">
                <p className="text-[9px] uppercase tracking-[1px] text-montalvo-gold font-medium mb-0.5" data-id="element-144">
                  ✦ Reserva reciente verificada
                </p>
                <p className="text-[13px] font-bold text-white truncate" data-id="element-145">
                  {socialProofClients[notifIndex].name}
                </p>
                <p className="text-[12px] text-[#888]" data-id="element-146">
                  {socialProofClients[notifIndex].service}
                </p>
                <p className="text-[11px] text-montalvo-gold mt-0.5" data-id="element-147">
                  {socialProofClients[notifIndex].time}
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* PROMO POPUP */}
      <AnimatePresence data-id="element-148">
        {showPopup && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-id="element-149">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} className="bg-montalvo-black border border-montalvo-gold rounded-sm p-8 max-w-md w-full relative text-center shadow-[0_0_40px_rgba(201,169,110,0.15)]" data-id="element-150">
              <button onClick={() => setShowPopup(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors" data-id="element-151">
                <X size={24} data-id="element-152" />
              </button>

              <h3 className="text-3xl font-serif text-montalvo-gold mb-4" data-id="element-153">
                ✦ Oferta Exclusiva
              </h3>
              <p className="text-white text-lg font-light mb-8 leading-relaxed" data-id="element-154">
                Agenda tu primera cita en Montalvo San Miguel y recibe una
                consultoría de imagen{' '}
                <strong className="text-montalvo-gold font-bold" data-id="element-155">GRATIS</strong>
              </p>

              <a href="https://wa.me/51932824708" target="_blank" rel="noopener noreferrer" onClick={() => setShowPopup(false)} className="block w-full bg-montalvo-gold hover:bg-montalvo-goldBright text-black py-4 rounded-sm text-sm font-bold uppercase tracking-widest transition-all duration-300 mb-4" data-id="element-156">
                Quiero Mi Consultoría Gratis
              </a>

              <p className="text-xs text-gray-500 uppercase tracking-widest" data-id="element-157">
                Solo por tiempo limitado
              </p>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}